import { useState } from 'react';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';

const CONTACT_URL = 'https://readdy.ai/api/form/d9m9tnu8mbnljin59lsg';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError('');

    const formEl = event.currentTarget;
    const fd = new FormData(formEl);
    const honeypot = (fd.get('phone_alt') as string | null)?.trim() || '';
    if (honeypot) {
      setStatus('success');
      formEl.reset();
      return;
    }
    fd.delete('phone_alt');

    const params = new URLSearchParams();
    fd.forEach((v, k) => {
      if (typeof v === 'string') params.append(k, v);
    });

    setStatus('submitting');
    try {
      const response = await fetch(CONTACT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = /spam/i.test(serverMsg);
      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setStatus('error');
      setFormError(err instanceof Error ? err.message : 'Network error. Please try again.');
    }
  }

  return (
    <div className="min-h-screen bg-white text-ink-900 antialiased">
      <Header />
      <main className="pt-28 md:pt-36 pb-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-ink-900 tracking-tight">Contact Us</h1>
          <p className="mt-4 text-lg text-ink-600 leading-relaxed">
            Have a question, suggestion, or want us to review a product? We&rsquo;d love to hear from you.
          </p>

          <div className="mt-10 grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <form
                data-readdy-form
                id="shopcentral-contact-form"
                onSubmit={handleSubmit}
                className="bg-ink-50 rounded-2xl p-6 md:p-8 space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-semibold text-ink-700 mb-1.5">
                      Name
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="Your name"
                      className="w-full h-11 px-4 rounded-xl bg-white border border-ink-200 outline-none text-sm text-ink-800 placeholder:text-ink-400 focus:border-cta-400 transition"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-semibold text-ink-700 mb-1.5">
                      Email
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="you@example.com"
                      className="w-full h-11 px-4 rounded-xl bg-white border border-ink-200 outline-none text-sm text-ink-800 placeholder:text-ink-400 focus:border-cta-400 transition"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-semibold text-ink-700 mb-1.5">
                    Subject
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    required
                    placeholder="What is this about?"
                    className="w-full h-11 px-4 rounded-xl bg-white border border-ink-200 outline-none text-sm text-ink-800 placeholder:text-ink-400 focus:border-cta-400 transition"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-sm font-semibold text-ink-700 mb-1.5">
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="Tell us what's on your mind..."
                    className="w-full px-4 py-3 rounded-xl bg-white border border-ink-200 outline-none text-sm text-ink-800 placeholder:text-ink-400 focus:border-cta-400 transition resize-none"
                  ></textarea>
                  <p className="mt-1 text-xs text-ink-400">Maximum 500 characters</p>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="cta-btn h-12 px-8 rounded-xl bg-cta-500 hover:bg-cta-600 disabled:opacity-70 disabled:cursor-not-allowed text-white text-sm font-semibold whitespace-nowrap transition flex items-center gap-2"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i> Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-send-plane-line text-base"></i>
                      </span>
                    </>
                  )}
                </button>

                {status === 'success' && (
                  <p className="flex items-center gap-2 text-sm text-emerald-600">
                    <i className="ri-check-line"></i> Message sent! We&rsquo;ll get back to you within 48 hours.
                  </p>
                )}
                {status === 'error' && formError && (
                  <p className="flex items-center gap-2 text-sm text-rose-600">
                    <i className="ri-error-warning-line"></i> {formError}
                  </p>
                )}
              </form>
            </div>

            <div className="space-y-6">
              <div className="bg-ink-50 rounded-2xl p-5">
                <p className="text-sm font-semibold text-ink-900 mb-2">Email</p>
                <a href="mailto:hello@buywize.net" className="text-sm text-cta-600 hover:text-cta-700 transition">
                  hello@buywize.net
                </a>
              </div>
              <div className="bg-ink-50 rounded-2xl p-5">
                <p className="text-sm font-semibold text-ink-900 mb-2">Response Time</p>
                <p className="text-sm text-ink-600">We typically respond within 24-48 hours during business days.</p>
              </div>
              <div className="bg-ink-50 rounded-2xl p-5">
                <p className="text-sm font-semibold text-ink-900 mb-2">Press Inquiries</p>
                <a href="mailto:press@buywize.net" className="text-sm text-cta-600 hover:text-cta-700 transition">
                  press@buywize.net
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}